import { DateTime } from "luxon";
import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import type { BaseError } from "@/domains/errors";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { LocalAudioRecordingBlobsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-blobs";
import { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import { errorHandlingModule } from "@/modules/error-handling";
import { logger } from "@/modules/logger";
import {
  DesktopMediaController,
  WebMediaController,
} from "@/store/audio/recording/media-controller";
import {
  MediaController,
  MediaControllerEventHandlers,
  MediaControllerInitializeOptions,
  MediaRecorderAudioChunk,
  MediaRecorderTranscriptTurn,
  MediaRecorderWaveformSamples,
  TranscriptionStatus,
} from "@/store/audio/recording/audio-recording.types";
import { IAudioRecordingStore } from "@/store/audio/recording/audio-recording.types";
import { AudioRecordingAutoStopManager } from "@/store/audio/recording/auto-stop";
import { AudioLevelProbe } from "@/modules/audio-recording/legacy";
import { AppStoreAudioUiStore } from "@/store/audio/ui/audio-ui.store";
import { AppSubStore, type AppSubStoreArgs } from "@/store/types";
import { AudioRecordingSilenceDetectionManager } from "@/store/audio/recording/silence-detection";
import { AudioRecordingRecord } from "@/domains/dexie-db/models/audio-recording/audio-recording";
import {
  DesktopPreparedRecordingController,
  PreparedRecording,
  PreparedRecordingController,
  WebPreparedRecordingController,
} from "@/store/audio/recording/prepared-recording-controller";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";

type AudioRecordingState = {
  controller: MediaController;
  initialBlobIndex: number;
  additionalBlobCount: number;
  initialTranscriptTurnOrder: number;
};

type RecordingStoreConfig = {
  onRecordingCompleted?: (args: { audioRecordingId: Uuid }) => void;
};

export class AppStoreAudioRecordingStore extends AppSubStore implements IAudioRecordingStore {
  visualizer: AudioLevelProbe | null = null;
  waveformSamples: MediaRecorderWaveformSamples | null = null;
  isMicrophoneDisconnected = false;
  activeAudioInputDeviceLabel: string | null = null;
  isAudioSilent = false;
  audioSilentSince: number | null = null;
  transcriptIsUnsupported = false;

  readonly ui: AppStoreAudioUiStore;
  lastError: BaseError | null = null;
  isPaused = true;
  isInitializing = false;

  private readonly audioRecordingStateMap = new Map<Uuid, AudioRecordingState>();
  private readonly erroredRecordingIds = new Set<Uuid>();
  private readonly onRecordingCompleted: ((args: { audioRecordingId: Uuid }) => void) | null;
  private readonly autoStopManager: AudioRecordingAutoStopManager;
  private readonly silenceDetectionManager: AudioRecordingSilenceDetectionManager;
  private readonly transcriptionStatusByRecording = new Map<Uuid, TranscriptionStatus>();
  private readonly preparedRecordingController: PreparedRecordingController;
  private preparedRecording: PreparedRecording | null = null;
  private preparedRecordingTimeout = 0;

  constructor(
    injectedDeps: AppSubStoreArgs & { uiStore: AppStoreAudioUiStore; config?: RecordingStoreConfig }
  ) {
    super(injectedDeps);

    this.ui = injectedDeps.uiStore;
    this.onRecordingCompleted = injectedDeps.config?.onRecordingCompleted ?? null;

    makeObservable<
      this,
      | "audioRecordingStateMap"
      | "onRecordingCompleted"
      | "audioRecordings"
      | "localAudioRecordingBlobs"
      | "localAudioRecordingTranscriptTurns"
      | "computeInitialIndexes"
      | "createController"
      | "createPreparedRecordingController"
      | "buildControllerEventHandlers"
      | "persistAudioChunk"
      | "persistTranscriptTurn"
      | "handleControllerError"
      | "handleControllerStop"
      | "disposeRecordingState"
      | "erroredRecordingIds"
      | "markRecordingAsErrored"
      | "initializeStateForRecording"
      | "visualizer"
      | "waveformSamples"
      | "isMicrophoneDisconnected"
      | "activeAudioInputDeviceLabel"
      | "setMicrophoneState"
      | "isAudioSilent"
      | "audioSilentSince"
      | "setVisualizer"
      | "setTranscriptionUnsupported"
      | "setTranscriptionStatus"
      | "autoStopManager"
      | "silenceDetectionManager"
      | "transcriptionStatusByRecording"
      | "preparedRecordingController"
      | "preparedRecording"
      | "preparedRecordingTimeout"
      | "PREPARED_RECORDING_TIMEOUT"
    >(this, {
      lastError: observable,
      isPaused: observable,
      visualizer: observable.ref,
      waveformSamples: observable.ref,
      isMicrophoneDisconnected: observable,
      activeAudioInputDeviceLabel: observable,
      isAudioSilent: observable,
      audioSilentSince: observable,
      isInitializing: observable,
      audioRecordingStateMap: observable.shallow,
      transcriptionStatusByRecording: observable.shallow,
      preparedRecordingController: observable,
      preparedRecording: false,
      preparedRecordingTimeout: false,
      debugActiveRecordings: computed,
      start: action.bound,
      pause: action.bound,
      resume: action.bound,
      stop: action.bound,
      clear: action.bound,
      ui: observable,
      onRecordingCompleted: observable,
      audioRecordings: computed,
      localAudioRecordingBlobs: computed,
      localAudioRecordingTranscriptTurns: computed,
      computeInitialIndexes: action,
      createController: action,
      getPreparedRecording: action,
      refreshPreparedRecording: action,
      createPreparedRecordingController: action,
      buildControllerEventHandlers: action,
      persistAudioChunk: action,
      persistTranscriptTurn: action,
      handleControllerError: action,
      handleControllerStop: action,
      disposeRecordingState: action,
      erroredRecordingIds: observable,
      markRecordingAsErrored: action,
      resetState: action,
      initializeStateForRecording: action,
      setMicrophoneState: action,
      setVisualizer: action,
      setTranscriptionUnsupported: action,
      transcriptIsUnsupported: observable,
      autoStopManager: observable,
      silenceDetectionManager: observable,
      getTranscriptionStatus: action,
      clearTranscriptionStatus: action,
      setTranscriptionStatus: action,
      getPersistedDurationMs: action,
      PREPARED_RECORDING_TIMEOUT: false,
      useInitializeAudioRecordingEffects: false,
    });

    this.preparedRecordingController = this.createPreparedRecordingController();

    this.autoStopManager = new AudioRecordingAutoStopManager({
      uiStore: this.ui,
      stopRecording: async ({ audioRecordingId }) => {
        await this.stop({ audioRecordingId });
      },
      getPersistedDurationMs: async audioRecordingId =>
        this.getPersistedDurationMs(audioRecordingId),
      isRecordingActive: audioRecordingId => this.audioRecordingStateMap.has(audioRecordingId),
    });

    this.silenceDetectionManager = new AudioRecordingSilenceDetectionManager({
      onStateChange: ({ isSilent, silentSince }) => {
        runInAction(() => {
          this.isAudioSilent = isSilent;
          this.audioSilentSince = silentSince;
        });
      },
    });
  }

  get debugActiveRecordings(): Array<{
    audioRecordingId: Uuid;
    hasController: boolean;
    initialBlobIndex: number;
    additionalBlobCount: number;
    initialTranscriptTurnOrder: number;
  }> {
    return Array.from(this.audioRecordingStateMap.entries()).map(([audioRecordingId, state]) => ({
      audioRecordingId,
      hasController: Boolean(state.controller),
      initialBlobIndex: state.initialBlobIndex,
      additionalBlobCount: state.additionalBlobCount,
      initialTranscriptTurnOrder: state.initialTranscriptTurnOrder,
    }));
  }

  private get audioRecordings(): AudioRecordingRepository {
    return this.store.dexie.repositories.audioRecordings;
  }

  private get localAudioRecordingBlobs(): LocalAudioRecordingBlobsRepository {
    return this.store.dexie.repositories.localAudioRecordingBlobs;
  }

  private get localAudioRecordingTranscriptTurns(): LocalAudioRecordingTranscriptTurnsRepository {
    return this.store.dexie.repositories.localAudioRecordingTranscriptTurns;
  }

  async start({
    preparedRecording,
    detectedMeetingWindowId,
    forceWebMediaController = false,
  }: {
    preparedRecording: PreparedRecording;
    detectedMeetingWindowId?: string;
    forceWebMediaController?: boolean;
  }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    const hadExistingState = this.audioRecordingStateMap.has(audioRecordingId);
    if (!hadExistingState) {
      runInAction(() => {
        this.isInitializing = true;
      });
    }

    await this.initializeStateForRecording({
      preparedRecording,
      detectedMeetingWindowId,
      onInitialized: async ({ audioRecording }) => {
        // Get the recall sdk upload id from the prepared recording credentials
        const recallUploadCredentials = await preparedRecording.recallUploadCredentials.catch(
          error => {
            logger.warn({
              message: "[AppStoreAudioRecordingStore] Failed to get recall upload credentials",
              info: { error: error instanceof Error ? error.message : String(error) },
            });
            return null;
          }
        );
        await this.audioRecordings.markAsRecordingStarted({
          id: audioRecording.id,
          recallSdkUploadId: recallUploadCredentials?.sdkUploadId,
        });
      },
      forceWebMediaController,
    });

    if (this.audioRecordingStateMap.has(audioRecordingId)) {
      await this.autoStopManager.beginMonitoring({ audioRecordingId });
    } else if (!hadExistingState) {
      runInAction(() => {
        this.isInitializing = false;
      });
    }
  }

  async pause({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);

    if (!state) {
      return;
    }

    await state.controller.pause();

    runInAction(() => {
      this.isPaused = true;
    });

    await this.audioRecordings.markAsPaused({ id: audioRecordingId });
  }

  async resume({ preparedRecording }: { preparedRecording: PreparedRecording }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    const state = this.audioRecordingStateMap.get(audioRecordingId);

    if (!state) {
      await this.start({ preparedRecording });
      return;
    }

    try {
      await state.controller.resume();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error({
        message: "[AppStoreAudioRecordingStore] resume failure",
        info: { error: errorMessage },
      });
      void this.audioRecordings.markAsResumingErrored({
        id: audioRecordingId,
        errorMessage,
      });
      throw error;
    }

    runInAction(() => {
      this.isPaused = false;
    });

    // Get the recall sdk upload id from the prepared recording credentials
    const recallUploadCredentials = await preparedRecording.recallUploadCredentials.catch(error => {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] Failed to get recall upload credentials on resume",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    });
    await this.audioRecordings.markAsResumed({
      id: audioRecordingId,
      recallSdkUploadId: recallUploadCredentials?.sdkUploadId,
    });
  }

  async stop({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);

    runInAction(() => {
      this.isPaused = false;
      this.isInitializing = false;
    });

    let didStopSuccessfully = false;

    /**
     * In some cases, they may not have a state (eg. if they just refreshed the page).
     */
    if (state) {
      try {
        await state.controller.stop();
        didStopSuccessfully = true;
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioRecordingStore] stop failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
        this.handleControllerError(audioRecordingId, { error });
      } finally {
        this.disposeRecordingState(audioRecordingId);
      }

      const hadControllerError = this.erroredRecordingIds.has(audioRecordingId);
      this.erroredRecordingIds.delete(audioRecordingId);

      if (!didStopSuccessfully || hadControllerError) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] stop failure",
          info: { audioRecordingId, didStopSuccessfully, hadControllerError },
        });

        return;
      }
    }

    await this.audioRecordings.markAsRecordingCompleted({ id: audioRecordingId });

    this.onRecordingCompleted?.({ audioRecordingId });
  }

  async clear({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.autoStopManager.endMonitoring({ audioRecordingId });

    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (state) {
      try {
        await state.controller.stop?.();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] clear stop failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
      this.disposeRecordingState(audioRecordingId);
    }

    try {
      await Promise.all([
        this.localAudioRecordingBlobs.deleteAllByRecordingId({
          associatedAudioRecordingId: audioRecordingId,
        }),
        this.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
          associatedAudioRecordingId: audioRecordingId,
        }),
      ]);
      this.clearTranscriptionStatus(audioRecordingId);
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioRecordingStore] clear failure",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    } finally {
      runInAction(() => {
        this.ui.resetRecording();
        this.isPaused = false;
        this.isInitializing = false;
        this.setVisualizer(null);
        this.waveformSamples = null;
        this.setMicrophoneState({ label: null, isDisconnected: false });
        this.clearTranscriptionStatus(audioRecordingId);
      });
    }
  }

  private async initializeStateForRecording({
    preparedRecording,
    detectedMeetingWindowId,
    onInitialized,
    forceWebMediaController,
  }: {
    preparedRecording: PreparedRecording;
    detectedMeetingWindowId?: string;
    onInitialized?: ({ audioRecording }: { audioRecording: AudioRecordingRecord }) => Promise<void>;
    forceWebMediaController?: boolean;
  }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    if (this.audioRecordingStateMap.has(audioRecordingId)) {
      return;
    }

    const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });
    if (!audioRecording) {
      return;
    }

    const { initialBlobIndex, initialTranscriptTurnOrder } =
      await this.computeInitialIndexes(audioRecordingId);
    const eventHandlers = this.buildControllerEventHandlers(audioRecordingId);

    const controller = this.createController(eventHandlers, preparedRecording, {
      forceWebMediaController,
    });
    const initializeOptions: MediaControllerInitializeOptions = {
      audioRecordingId: audioRecording.id,
      noteId: audioRecording.data.associated_note_id ?? null,
      detectedMeetingWindowId,
      onBeginRecording: () => {
        this.ui.beginRecording();
      },
      onFallbackToWebMedia: () => {
        this.store.audio.trashRecording({ audioRecordingId });
        this.ui.promptSystemAudioRecordingFailed({
          audioRecordingId,
          noteId: audioRecording.data.associated_note_id ?? null,
          onConfirm: async () => {
            await this.store.audio.createAndStartAudioRecording({
              associatedNoteId: audioRecording.data.associated_note_id ?? null,
              associatedMeetingWindowId: detectedMeetingWindowId,
              config: {
                showOnboardingTooltip: false,
                forceWebMediaController: true,
              },
            });
          },
          onRetry: async () => {
            await this.store.audio.createAndStartAudioRecording({
              associatedNoteId: audioRecording.data.associated_note_id ?? null,
              associatedMeetingWindowId: detectedMeetingWindowId,
              config: {
                showOnboardingTooltip: false,
                forceWebMediaController: false,
              },
            });
          },
        });
      },
    };

    try {
      await controller.initializeAndStart(initializeOptions);

      const controllerVisualizer = controller.getVisualizer?.() ?? null;

      runInAction(() => {
        this.lastError = null;
        this.setVisualizer(controllerVisualizer);
        this.waveformSamples = null;
        this.isMicrophoneDisconnected = false;
        this.activeAudioInputDeviceLabel = null;
        this.isPaused = false;
        this.isInitializing = false;
        this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
      });

      this.audioRecordingStateMap.set(audioRecordingId, {
        controller,
        initialBlobIndex,
        additionalBlobCount: 0,
        initialTranscriptTurnOrder,
      });

      await onInitialized?.({ audioRecording });
    } catch (unknownErr) {
      controller.dispose();

      const parsedError = errorHandlingModule.parseError({ unknownError: unknownErr });

      logger.error({
        message: "[AppStoreAudioRecordingStore] initialize state for recording failure",
        info: { error: objectModule.safeErrorAsJson(parsedError) },
      });

      runInAction(() => {
        this.lastError = parsedError;
        this.ui.resetRecording();
        this.isPaused = false;
        this.isInitializing = false;
      });
    }
  }

  private async computeInitialIndexes(audioRecordingId: Uuid): Promise<{
    initialBlobIndex: number;
    initialTranscriptTurnOrder: number;
  }> {
    const [blobs, transcripts] = await Promise.all([
      this.localAudioRecordingBlobs.getAllByRecordingId({
        associatedAudioRecordingId: audioRecordingId,
      }),
      this.localAudioRecordingTranscriptTurns.getAllByRecordingId({
        associatedAudioRecordingId: audioRecordingId,
      }),
    ]);
    if (blobs.length === 0) {
      // Erase transcripts if there are no blobs to avoid the inconsistency.
      // This scenario can happen if the page is reloaded after starting to record.
      if (transcripts.length > 0) {
        logger.info({
          message: "[AppStoreAudioRecordingStore] clearing transcripts due to no blobs",
          info: { audioRecordingId, transcriptsCount: transcripts.length },
        });
        await this.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
          associatedAudioRecordingId: audioRecordingId,
        });
      }
      return {
        initialBlobIndex: 0,
        initialTranscriptTurnOrder: 0,
      };
    }

    return {
      initialBlobIndex: blobs.length ? blobs[blobs.length - 1].blobIndex + 1 : 0,
      initialTranscriptTurnOrder: transcripts.length
        ? transcripts[transcripts.length - 1].transcriptTurnIndex + 1
        : 0,
    };
  }

  async getPersistedDurationMs(audioRecordingId: Uuid): Promise<number> {
    const blobs = await this.localAudioRecordingBlobs.getAllByRecordingId({
      associatedAudioRecordingId: audioRecordingId,
    });
    return blobs.reduce((total, blob) => total + (blob.durationMs ?? 0), 0);
  }

  private createController(
    eventHandlers: MediaControllerEventHandlers,
    preparedRecording: PreparedRecording,
    options?: { forceWebMediaController?: boolean }
  ): MediaController {
    if (this.store.interface.isDesktop && !options?.forceWebMediaController) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       * (Instead, we fall back to the web-based media controller.)
       */
      if (!this.store.electron.isIntelMacDesktop) {
        return new DesktopMediaController({
          eventHandlers,
          preparedRecording,
        });
      }
    }

    const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
    return new WebMediaController({
      eventHandlers,
      customVocabulary,
    });
  }

  private createPreparedRecordingController(): PreparedRecordingController {
    if (this.store.interface.isDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       * (Instead, we fall back to the web-based media controller.)
       */
      if (!this.store.electron.isIntelMacDesktop) {
        return new DesktopPreparedRecordingController();
      }
    }

    return new WebPreparedRecordingController();
  }

  getPreparedRecording(audioRecordingId?: Uuid): PreparedRecording {
    try {
      if (
        this.preparedRecording &&
        this.preparedRecording.expiresAt >= DateTime.local() &&
        (!audioRecordingId || this.preparedRecording.audioRecordingId === audioRecordingId)
      ) {
        return this.preparedRecording;
      }

      const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
      return this.preparedRecordingController.prepareRecording({
        customVocabulary,
        audioRecordingId: audioRecordingId ?? uuidModule.generate(),
      });
    } finally {
      // Don't reuse the same prepared recording.
      this.refreshPreparedRecording();
    }
  }

  private readonly PREPARED_RECORDING_TIMEOUT = 3600_000; // 1 hour

  refreshPreparedRecording(): void {
    logger.info({
      message: "[AppStoreAudioRecordingStore] Refresh prepared recording",
    });
    clearTimeout(this.preparedRecordingTimeout);

    // Use active recording id.
    const audioRecordingId = this.store.audio.activeRecordingId;

    if (audioRecordingId && !this.isPaused) {
      return;
    }

    const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
    this.preparedRecording = this.preparedRecordingController.prepareRecording({
      customVocabulary,
      audioRecordingId: audioRecordingId ?? uuidModule.generate(),
    });

    this.preparedRecordingTimeout = window.setTimeout(() => {
      // Refresh the prepared recording after 1 hour to avoid stale data.
      this.refreshPreparedRecording();
    }, this.PREPARED_RECORDING_TIMEOUT);
  }

  private buildControllerEventHandlers(audioRecordingId: Uuid): MediaControllerEventHandlers {
    return {
      onAudioChunk: ({ chunk }) => {
        void this.persistAudioChunk({ audioRecordingId, chunk });
      },
      onTranscriptTurn: ({ turn }) => {
        runInAction(() => {
          this.setTranscriptionUnsupported(false);
          this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
        });
        void this.persistTranscriptTurn({ audioRecordingId, turn });
      },
      onWaveformSamples: ({ samples }) => {
        runInAction(() => {
          this.waveformSamples = samples;
        });
      },
      onError: ({ error }) => {
        this.handleControllerError(audioRecordingId, { error });
      },
      onStop: () => {
        this.handleControllerStop(audioRecordingId);
      },
      onTranscriptionUnavailable: () => {
        runInAction(() => {
          this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Disconnected);
        });
      },
      onTranscriptionUnsupported: () => {
        runInAction(() => {
          this.setTranscriptionUnsupported(true);
        });
      },
      onTranscriptionStatusChange: ({ status }) => {
        runInAction(() => {
          this.setTranscriptionStatus(audioRecordingId, status);
        });
      },
      onVoiceDisconnected: () => {
        void this.audioRecordings.markVoiceDisconnected({ id: audioRecordingId });
      },
      onVoiceReconnected: () => {
        void this.audioRecordings.markVoiceReconnected({ id: audioRecordingId });
      },
      onDebugEvent: ({ data, kind }) => {
        this.store.electron.pushRecallDebugEvent(data, kind);
      },
    };
  }

  private async persistAudioChunk({
    audioRecordingId,
    chunk,
  }: {
    audioRecordingId: Uuid;
    chunk: MediaRecorderAudioChunk;
  }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    state.additionalBlobCount = state.additionalBlobCount + 1;

    const blobIndex = state.initialBlobIndex + state.additionalBlobCount;

    await this.localAudioRecordingBlobs.upsert({
      associatedAudioRecordingId: audioRecordingId,
      blobIndex,
      data: {
        rawBlob: chunk.blob,
        byteLength: chunk.byteLength,
        durationMs: chunk.durationMs,
        isUploaded: false,
      },
    });

    this.autoStopManager.handleChunk({
      audioRecordingId,
      durationMs: chunk.durationMs,
    });
  }

  private async persistTranscriptTurn({
    audioRecordingId,
    turn,
  }: {
    audioRecordingId: Uuid;
    turn: MediaRecorderTranscriptTurn;
  }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    const transcriptTurnIndex = state.initialTranscriptTurnOrder + turn.turnOrder;

    await this.localAudioRecordingTranscriptTurns.upsert({
      associatedAudioRecordingId: audioRecordingId,
      transcriptTurnIndex: transcriptTurnIndex,
      data: {
        channel: turn.channel,
        content: {
          turnTimestamp: DateTime.fromMillis(turn.timestampMs),
          turnOrder: turn.turnOrder,
          isFormatted: false,
          isEndOfTurn: true,
          endOfTurnConfidence: 1,
          words: (turn.words ?? []).map(word => ({
            text: word.word,
            start: word.startMs,
            end: word.endMs,
            confidence: 1,
          })),
        },
      },
    });
  }

  private async markRecordingAsErrored({
    audioRecordingId,
    errorMessage,
  }: {
    audioRecordingId: Uuid;
    errorMessage?: string | null;
  }): Promise<void> {
    try {
      await this.audioRecordings.markAsRecordingErrored({
        id: audioRecordingId,
        errorMessage,
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] failed to mark recording as errored",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  private handleControllerError(audioRecordingId: Uuid, { error }: { error: unknown }): void {
    const parsedError = errorHandlingModule.parseError({ unknownError: error });
    this.erroredRecordingIds.add(audioRecordingId);
    void this.markRecordingAsErrored({
      audioRecordingId,
      errorMessage: parsedError.message,
    });
    this.disposeRecordingState(audioRecordingId);
    runInAction(() => {
      this.lastError = parsedError;
      this.isPaused = false;
      this.isInitializing = false;
    });
  }

  private handleControllerStop(audioRecordingId: Uuid): void {
    this.autoStopManager.handleControllerStop({ audioRecordingId });

    this.disposeRecordingState(audioRecordingId);
    runInAction(() => {
      this.isPaused = false;
      this.isInitializing = false;
    });
  }

  private disposeRecordingState(audioRecordingId: Uuid): void {
    this.autoStopManager.endMonitoring({ audioRecordingId });

    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    try {
      state.controller.dispose();
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] dispose failure",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }

    this.audioRecordingStateMap.delete(audioRecordingId);

    runInAction(() => {
      this.setVisualizer(null);
      this.waveformSamples = null;
      this.isMicrophoneDisconnected = false;
      this.activeAudioInputDeviceLabel = null;
      this.setTranscriptionUnsupported(false);
      this.isInitializing = false;
    });
  }

  resetState() {
    this.audioRecordingStateMap.forEach(state => {
      try {
        state.controller.dispose();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] resetState dispose failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    });
    this.audioRecordingStateMap.clear();
    this.erroredRecordingIds.clear();
    this.lastError = null;
    this.setTranscriptionUnsupported(false);
    this.transcriptionStatusByRecording.clear();
    this.autoStopManager.reset();
    this.silenceDetectionManager.reset();
    this.isInitializing = false;
  }

  setMicrophoneState({
    label,
    isDisconnected,
  }: {
    label?: string | null;
    isDisconnected?: boolean;
  }): void {
    if (label !== undefined) {
      this.activeAudioInputDeviceLabel = label;
    }

    if (typeof isDisconnected === "boolean") {
      this.isMicrophoneDisconnected = isDisconnected;
    }
  }

  setVisualizer(probe: AudioLevelProbe | null): void {
    if (this.visualizer === probe) {
      return;
    }

    if (this.visualizer && this.visualizer !== probe) {
      try {
        this.visualizer.dispose();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] setVisualizer dispose failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    }

    this.visualizer = probe;
    this.silenceDetectionManager.attachProbe({ audioProbe: probe });
  }
  setTranscriptionUnsupported(value: boolean): void {
    this.transcriptIsUnsupported = value;
  }

  setTranscriptionStatus(audioRecordingId: Uuid, status: TranscriptionStatus): void {
    const current = this.transcriptionStatusByRecording.get(audioRecordingId);
    if (current === TranscriptionStatus.Compromised && status !== TranscriptionStatus.Compromised) {
      return;
    }

    // Track transcription disconnect/reconnect events
    const wasConnected = current === TranscriptionStatus.Live;
    const isNowDisconnected =
      status === TranscriptionStatus.Disconnected || status === TranscriptionStatus.Compromised;
    const isNowConnected = status === TranscriptionStatus.Live;
    const wasDisconnected =
      current === TranscriptionStatus.Disconnected || current === TranscriptionStatus.Compromised;

    if (wasConnected && isNowDisconnected) {
      void this.audioRecordings.markTranscriptionDisconnected({ id: audioRecordingId });
    } else if (wasDisconnected && isNowConnected) {
      void this.audioRecordings.markTranscriptionReconnected({ id: audioRecordingId });
    }

    // Track when transcription becomes compromised (after max reconnect attempts)
    if (status === TranscriptionStatus.Compromised && current !== TranscriptionStatus.Compromised) {
      void this.audioRecordings.markTranscriptionCompromised({ id: audioRecordingId });
    }

    this.transcriptionStatusByRecording.set(audioRecordingId, status);
  }

  getTranscriptionStatus(audioRecordingId: Uuid): TranscriptionStatus {
    // Check app_events first - compromised is a terminal/sticky state that takes precedence
    // This ensures we don't lose the compromised status after page refresh
    const recording = this.store.audioRecordings.get(audioRecordingId);
    if (recording?.wasTranscriptionCompromised) {
      return TranscriptionStatus.Compromised;
    }

    // Then check in-memory map for real-time status during active recording
    return this.transcriptionStatusByRecording.get(audioRecordingId) ?? TranscriptionStatus.Live;
  }

  clearTranscriptionStatus(audioRecordingId: Uuid): void {
    this.transcriptionStatusByRecording.delete(audioRecordingId);
  }

  useInitializeAudioRecordingEffects(): void {
    useEffectOnMount(() => {
      // Prepare for the first recording.
      this.refreshPreparedRecording();

      const reactionDisposers = [
        reaction(
          () => this.store.spaceAccountSettings.customVocabulary,
          () => {
            // Refresh the prepared recording when the custom vocabulary changes.
            this.refreshPreparedRecording();
          }
        ),
        reaction(
          () => this.store.audio.activeRecordingId,
          () => {
            // Refresh the prepared recording when the custom vocabulary changes.
            this.refreshPreparedRecording();
          }
        ),
      ];
      return () => reactionDisposers.forEach(f => f());
    });
  }
}
